import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { staticText } from "../../statics/static-text";

// TEXT
export const text = staticText.components.tocNav;

// STYLES

// DATA

// TYPES
export type Props = { flex?: number };

export type TOCAnchorPointMeta = {
  label: string;
  hashKey: string;
  yCoord: number;
};

// CONSTANTS
export const TOC_ANCHOR_CLASS = "__toc-anchor__";
export const NAV_ITEM_KEY_PREFIX = "toc-nav-item__";

// FUNCTIONS
export const useAchorPoints = () => {
  const [anchorPoints, setAnchorPoints] = useState<TOCAnchorPointMeta[]>([]);

  /**
   * Search the DOM for elements with TOC_ANCHOR_CLASS and
   * generate anchor point meta. Meta is then passed to
   * TOCNavMenuInner for rendering and advanced logic.
   */
  const getAnchorPoints = useCallback(() => {
    const sectionElements = Array.from(
      document.getElementsByClassName(TOC_ANCHOR_CLASS),
    );

    const _anchorPoints: TOCAnchorPointMeta[] = [];
    sectionElements.forEach((element, index) => {
      _anchorPoints.push({
        label: element.getAttribute("aria-label") ?? `Section ${index + 1}`,
        hashKey: element.id.replace(TOC_ANCHOR_CLASS, ""),
        yCoord: element.getBoundingClientRect().top,
      });
    });

    setAnchorPoints(_anchorPoints);
  }, []);

  /**
   * The MutationObserver allows us to watch for a few different
   * events, including page resizing when new elements might be
   * added to the page (potentially changing the location of our
   * anchor points)
   */
  useEffect(() => {
    const observer = new MutationObserver(() => getAnchorPoints());
    const rootNode = document.getElementById("root");

    if (rootNode) {
      // Run it once initially
      getAnchorPoints();

      // Then let observer take over
      observer.observe(rootNode, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, [getAnchorPoints]);

  return anchorPoints;
};

export const useActiveTOCSection = (anchorPoints: TOCAnchorPointMeta[]) => {
  const navigate = useNavigate();
  const { hash, state: locationState } = useLocation();

  const isScrolling = useRef(false); // Flag to track if a programmatic scroll operation is in progress
  const initialScrollTo = useRef(Boolean(hash));
  const observer = useRef<IntersectionObserver | null>(null);

  const [activeSection, setActiveSection] = useState<TOCAnchorPointMeta>();

  useEffect(() => {
    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      if (isScrolling.current) return; // Disable observer during programmatic scroll

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const intersectingSection = anchorPoints.find(
            (anchor) =>
              anchor.hashKey === entry.target.id.replace(TOC_ANCHOR_CLASS, ""),
          );
          if (intersectingSection) {
            setActiveSection(intersectingSection);
            navigate(`#${intersectingSection.hashKey}`, { replace: true });
          }
        }
      });
    };

    // Calculate the bottom margin value in JavaScript
    // 287px = nav bar + padding above TOC + "Personal Info" header in TOC + flex gap in TOC
    // Lining up the intersection with the Security header in TOC
    const bottomMargin = window.innerHeight - 287;
    const rootMargin = `-287px 0px -${bottomMargin}px 0px`;

    observer.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin,
      threshold: 0,
    });

    const elements = document.querySelectorAll(`.${TOC_ANCHOR_CLASS}`);
    elements.forEach((elem) => observer.current?.observe(elem));

    return () => observer.current?.disconnect();
  }, [anchorPoints, navigate]);

  useEffect(() => {
    if (!activeSection) return;

    if (initialScrollTo.current === true || locationState?.scrollTo === true) {
      const target = document.getElementById(
        TOC_ANCHOR_CLASS + activeSection?.hashKey,
      );
      const scrollWindow = document.getElementById("scrollWindow");

      isScrolling.current = true; // Set the flag before starting the scroll

      if ("onscrollend" in window && scrollWindow) {
        scrollWindow.addEventListener(
          "scrollend",
          () => (isScrolling.current = false),
          { once: true },
        );
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          isScrolling.current = false; // Clear the flag after the scroll is complete
        }, 500);
      }

      initialScrollTo.current = false;
    }
  }, [activeSection, initialScrollTo, locationState]);

  useEffect(() => {
    const parsedHash = hash.replace("#", "");

    if (activeSection?.hashKey !== parsedHash) {
      const hashMatch = anchorPoints.find((m) => m.hashKey === parsedHash);

      setActiveSection(hashMatch);
    }
  }, [activeSection, anchorPoints, hash]);

  return activeSection;
};

// ASSETS
